import type { Budget, BudgetPeriod, CostsState } from '@orgus/protocol';
import { formatUsd } from '@orgus/protocol';
import { useMemo, useState } from 'react';
import { Button, ConfirmDialog, Dialog, ErrorState, Input, Segmented, Select, toUiError } from '@orgus/ui';
import type { SelectOption, UiError } from '@orgus/ui';
import { useProjects } from '../../lib/store.ts';
import { parseLimit, parseSoftPercent, scopeFromKey, scopeKey } from './model.ts';
import { deleteBudget, saveBudget } from './useCosts.ts';

type ScopeKind = 'org' | 'project' | 'mission' | 'session';

const KINDS: { value: ScopeKind; label: string }[] = [
  { value: 'org', label: 'Everything' },
  { value: 'project', label: 'Project' },
  { value: 'mission', label: 'Mission' },
  { value: 'session', label: 'Session' },
];

const PERIODS: { value: BudgetPeriod; label: string }[] = [
  { value: 'day', label: 'Daily' },
  { value: 'month', label: 'Monthly' },
  { value: 'total', label: 'Total' },
];

/** Create or edit a budget. `budget` set means edit (with Delete). */
export function BudgetDialog({
  open,
  onOpenChange,
  budget,
  costs,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  budget: Budget | null;
  costs: CostsState;
}) {
  // Remount per budget so the form starts from its values.
  return open ? <BudgetForm key={budget?.id ?? 'new'} onOpenChange={onOpenChange} budget={budget} costs={costs} /> : null;
}

function BudgetForm({ onOpenChange, budget, costs }: { onOpenChange: (v: boolean) => void; budget: Budget | null; costs: CostsState }) {
  const projects = useProjects();
  const [scope, setScope] = useState(budget ? scopeKey(budget) : 'org');
  const kind = (scope.split(':')[0] || 'org') as ScopeKind;
  const [period, setPeriod] = useState<BudgetPeriod>(budget?.period ?? 'month');
  const [limit, setLimit] = useState(budget ? String(budget.limit) : '');
  const [soft, setSoft] = useState(String(Math.round((budget?.softAt ?? 0.8) * 100)));
  const [onLimit, setOnLimit] = useState<'warn' | 'stop'>(budget?.onLimit ?? 'warn');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // One list per kind, so the picker never mixes projects, missions and sessions.
  const choices = useMemo(() => {
    const byKind: Record<Exclude<ScopeKind, 'org'>, SelectOption[]> = {
      project: projects.map((p) => ({ value: `project:${p.id}`, label: p.name })),
      mission: costs.byMission.map((m) => ({ value: `mission:${m.key}`, label: m.label })),
      session: costs.sessions.slice(0, 30).map((x) => ({ value: `session:${x.sessionId}`, label: x.title })),
    };
    if (kind !== 'org' && scope.length > kind.length + 1 && !byKind[kind].some((x) => x.value === scope)) {
      byKind[kind].push({ value: scope, label: scope.slice(kind.length + 1) });
    }
    return byKind;
  }, [projects, costs.byMission, costs.sessions, scope, kind]);

  const pickKind = (k: ScopeKind) => {
    if (k === kind) return;
    setScope(k === 'org' ? 'org' : (choices[k][0]?.value ?? `${k}:`));
  };

  const limitValue = parseLimit(limit);
  const softValue = parseSoftPercent(soft);
  const limitError = touched && limitValue === null ? 'Enter a dollar amount above $0, for example 250.' : undefined;
  const softError = touched && softValue === null ? 'Enter a percent between 1 and 99.' : undefined;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    const sc = scopeFromKey(scope);
    if (!sc || limitValue === null || softValue === null) return;
    setBusy(true);
    setError(null);
    try {
      await saveBudget({ scope: sc, period, limit: limitValue, softAt: softValue, onLimit }, budget?.id);
      onOpenChange(false);
    } catch (err) {
      setError(toUiError(err, { what: 'The budget was not saved.' }));
    } finally {
      setBusy(false);
    }
  }

  const kindLabel = KINDS.find((k) => k.value === kind)?.label ?? '';

  return (
    <>
      <Dialog
        open
        onOpenChange={onOpenChange}
        title={budget ? 'Edit budget' : 'Add a budget'}
        footer={
          <>
            {budget && (
              <Button variant="ghost" className="mr-auto text-failed" onClick={() => setConfirmDelete(true)}>
                Delete budget
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="budget-form" loading={busy}>
              {budget ? 'Save budget' : 'Add budget'}
            </Button>
          </>
        }
      >
        <form id="budget-form" className="flex flex-col gap-4" onSubmit={submit} noValidate>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-muted">Applies to</span>
            <Segmented label="Applies to" options={KINDS} value={kind} onChange={pickKind} size="md" />
          </div>
          {kind !== 'org' && (
            <Select
              aria-label={kindLabel}
              options={choices[kind]}
              value={scope}
              onValueChange={setScope}
              placeholder={choices[kind].length ? `Choose a ${kindLabel.toLowerCase()}` : `No ${kindLabel.toLowerCase()}s yet`}
              disabled={!choices[kind].length}
            />
          )}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-muted">Period</span>
            <Segmented label="Period" options={PERIODS} value={period} onChange={setPeriod} size="md" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Limit (USD)"
              inputMode="decimal"
              placeholder="250"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              onBlur={() => setTouched(true)}
              error={limitError}
              icon={<span className="text-fg-subtle">$</span>}
            />
            <Input
              label="Warn at (% of limit)"
              inputMode="numeric"
              value={soft}
              onChange={(e) => setSoft(e.target.value)}
              onBlur={() => setTouched(true)}
              error={softError}
              hint={limitValue !== null && softValue !== null ? `Warns at ${formatUsd(limitValue * softValue)}` : undefined}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-muted">At the limit</span>
            <Segmented
              label="At the limit"
              options={[
                { value: 'warn', label: 'Warn only' },
                { value: 'stop', label: 'Stop launched sessions' },
              ]}
              value={onLimit}
              onChange={setOnLimit}
              size="md"
            />
            {onLimit === 'stop' && <span className="text-xs text-fg-subtle">Sessions started in a terminal keep running.</span>}
          </div>
          {error && <ErrorState error={error} variant="inline" />}
        </form>
      </Dialog>
      {budget && (
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this budget?"
          description="Spend history stays."
          confirmLabel="Delete budget"
          onConfirm={async () => {
            await deleteBudget(budget.id);
            onOpenChange(false);
          }}
        />
      )}
    </>
  );
}
